import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2 } from "lucide-react";
import { uploadMedia } from "../lib/api";

// Reusable file & media uploader with chunked transfer + progress.
// Props: contextType, contextId, visibility ("Private"|"Shared"),
//   showVisibility (bool), accept (string), label (string), onUploaded(asset), testid
export default function MediaUploader({
  contextType = "library", contextId = "", visibility = "Private",
  showVisibility = false, accept = "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt",
  label = "Upload file", onUploaded, testid = "media-uploader",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [vis, setVis] = useState(visibility);

  const pick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setPct(0);
    try {
      for (const f of files) {
        const asset = await uploadMedia(f, { contextType, contextId, visibility: vis, onProgress: setPct });
        onUploaded && onUploaded(asset);
      }
      toast.success(files.length > 1 ? `${files.length} files uploaded` : "File uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false); setPct(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid={testid}>
      <input ref={inputRef} type="file" multiple accept={accept} onChange={onFiles} className="hidden" data-testid={`${testid}-input`} />
      {showVisibility && (
        <select value={vis} onChange={(e) => setVis(e.target.value)} className="max-w-[130px]" data-testid={`${testid}-visibility`}>
          <option value="Private">Private</option>
          <option value="Shared">Shared</option>
        </select>
      )}
      <button className="btn-outline flex items-center gap-2 text-sm" onClick={pick} disabled={busy} data-testid={`${testid}-btn`}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
        {busy ? `Uploading… ${pct}%` : label}
      </button>
      {busy && (
        <div className="h-1.5 w-40 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: "var(--primary)" }} />
        </div>
      )}
    </div>
  );
}
